/**
 * Advanced filter/sort toolbar for the Members directory (Task 1.1).
 * Renders search, status, plan, age-group, gender, and expiry-sort controls.
 * All state lives in MembersPage; this component is purely presentational.
 */
import { plan_type, type MembershipStatus, type PlanType, type types } from '@gym/shared';
import { ArrowDown, ArrowDownUp, ArrowUp } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/Button.js';
import { Select } from '../../components/ui/Field.js';
import { SearchInput, Toolbar } from '../../components/ui/Layout.js';

const AGE_GROUPS: types.AgeGroup[] = ['under_18', '18_30', '31_45', '46_60', 'over_60'];
const GENDERS = ['male', 'female', 'other', 'unspecified'] as const;

export type ExpirySort = 'none' | 'soonest' | 'latest';

export interface MemberFiltersToolbarProps {
  search: string;
  onSearch: (v: string) => void;
  status: MembershipStatus | 'all';
  onStatus: (v: MembershipStatus | 'all') => void;
  statusOptions: (MembershipStatus | 'all')[];
  plan: PlanType | 'all';
  onPlan: (v: PlanType | 'all') => void;
  ageGroup: types.AgeGroup | 'all';
  onAgeGroup: (v: types.AgeGroup | 'all') => void;
  gender: (typeof GENDERS)[number] | 'all';
  onGender: (v: (typeof GENDERS)[number] | 'all') => void;
  expirySort: ExpirySort;
  onCycleExpirySort: () => void;
}

export function MemberFiltersToolbar({
  search,
  onSearch,
  status,
  onStatus,
  statusOptions,
  plan,
  onPlan,
  ageGroup,
  onAgeGroup,
  gender,
  onGender,
  expirySort,
  onCycleExpirySort,
}: MemberFiltersToolbarProps): ReactNode {
  const { t } = useTranslation();

  const expirySortIcon =
    expirySort === 'soonest' ? (
      <ArrowDown className="h-4 w-4" aria-hidden />
    ) : expirySort === 'latest' ? (
      <ArrowUp className="h-4 w-4" aria-hidden />
    ) : (
      <ArrowDownUp className="h-4 w-4" aria-hidden />
    );

  return (
    <Toolbar className="rounded-xl border border-border bg-surface p-3">
      <SearchInput
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder={t('members.search')}
        className="min-w-[14rem] flex-1"
        aria-label={t('members.search')}
      />

      {/* Status */}
      <Select
        value={status}
        onChange={(e) => onStatus(e.target.value as MembershipStatus | 'all')}
        className="w-auto"
        aria-label={t('memberships.title')}
      >
        {statusOptions.map((s) => (
          <option key={s} value={s}>
            {s === 'all' ? t('common.all') : t(`memberships.status.${s}`)}
          </option>
        ))}
      </Select>

      {/* Plan */}
      <Select
        value={plan}
        onChange={(e) => onPlan(e.target.value as PlanType | 'all')}
        className="w-auto"
        aria-label={t('members.filters.plan')}
      >
        <option value="all">{t('common.all')}</option>
        {plan_type.map((pt) => (
          <option key={pt} value={pt}>
            {t(`plans.type.${pt}`, { defaultValue: pt })}
          </option>
        ))}
      </Select>

      {/* Age group */}
      <Select
        value={ageGroup}
        onChange={(e) => onAgeGroup(e.target.value as types.AgeGroup | 'all')}
        className="w-auto"
        aria-label={t('members.filters.ageGroup')}
      >
        <option value="all">{t('common.all')}</option>
        {AGE_GROUPS.map((ag) => (
          <option key={ag} value={ag}>
            {t(`members.ageGroup.${ag}`, { defaultValue: ag })}
          </option>
        ))}
      </Select>

      {/* Gender */}
      <Select
        value={gender}
        onChange={(e) => onGender(e.target.value as (typeof GENDERS)[number] | 'all')}
        className="w-auto"
        aria-label={t('members.filters.gender')}
      >
        <option value="all">{t('common.all')}</option>
        {GENDERS.map((g) => (
          <option key={g} value={g}>
            {t(`members.gender.${g}`, { defaultValue: g })}
          </option>
        ))}
      </Select>

      {/* Expiry sort cycle: none → soonest → latest → none */}
      <Button
        variant={expirySort !== 'none' ? 'brand' : 'secondary'}
        size="md"
        icon={expirySortIcon}
        onClick={onCycleExpirySort}
        aria-label={t('members.filters.expirySort')}
        aria-pressed={expirySort !== 'none'}
      >
        {expirySort === 'soonest'
          ? t('members.filters.expirySortSoonest')
          : expirySort === 'latest'
            ? t('members.filters.expirySortLatest')
            : t('members.filters.expirySort')}
      </Button>
    </Toolbar>
  );
}
